/**
 * @author Mauricio Valencia.
 */
import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import './styles.css';
import closeIconRsr from './close.svg';

/**
 * @Settings
 *
 */

/**
 * @name BannerSticky
 * @param props {Object} [component , textOnTab, width, height].
 * {props.component} : [jsx] component.
 * {props.textOnTab} : [string] text on tab.
 * {props.width} : [string] width of banner
 * {props.height} : [string] height of banner
 * {props.isExpandBannerOn} : [boolean] banner expanded on/off
 * {props.isShownHiderButtonOn} : [boolean] hider button on/off
 * {props.isShownBannerOn} : [boolean] hide banner on/off
 * @returns {JSX.Element}
 * @constructor
 * @Description This Component, could be used for display animated
 * retractive banner, can be receive any type of component
 * under composition pattern, besides itself attributes.
 */
const BannerSticky = (props) => {
  const {
    component,
    textOnTab,
    width,
    height,
    isExpandBannerOn,
    isShownHiderButtonOn,
    isShownBannerOn,
    bannerReference,

    bannerIsShownId,
    bannerExpandedId,
  } = props?.banner;

  /**
   * Constants
   */
  let widthChange = '';
  const TAB_WIDTH = '38px'; //tab-width by default
  const EXPAND_RETRACTIVE_BANNER = 'EXPAND_RETRACTIVE_BANNER';
  const HIDE_BANNER = 'HIDE_BANNER';
  const SHOW_HIDER_BUTTON = 'SHOW_HIDER_BUTTON';
  const HIDE_HIDER_BUTTON = 'HIDE_HIDER_BUTTON';

  /**
   * Handlers
   */
  const expandHandler = () => {
    widthChange = width;
    return 'BannerStickyBoxExpand';
  };

  const retractHandler = () => {
    widthChange = TAB_WIDTH;
    return 'BannerStickyBoxRetract';
  };

  const hideBannerHandler = () => {
    return 'BannerStickyDisplayHide';
  };

  const showBannerHiderButtonHandler = () => {
    return 'BannerStickyDisplayShow';
  };

  const hideBannerHiderButtonHandler = () => {
    return 'BannerStickyDisplayHide';
  };

  /**
   * Reducers Actions.
   */
  const doSetIsExpandBanner = (isExpand) =>
    isExpand ? expandHandler() : retractHandler();

  const doSetIsShownHiderButton = (isShow) =>
    isShow ? showBannerHiderButtonHandler() : hideBannerHiderButtonHandler();

  const doSetIsShownBanner = (isShow) => {
    return isShow ? '' : hideBannerHandler();
  };

  const reducer = (stateParam, action) => {
    switch (action.type) {
      case EXPAND_RETRACTIVE_BANNER:
        return {
          ...stateParam,
          toggleExpandBanner: !stateParam.toggleExpandBanner,
          isExpandBanner: doSetIsExpandBanner(!stateParam.toggleExpandBanner),
          isExpandBannerMark: !stateParam.toggleExpandBanner,
        };
      case HIDE_BANNER:
        return {
          ...stateParam,
          isShownBanner: doSetIsShownBanner(false),
          isShownBannerMark: false,
          isExpandBannerMark: false,
        };
      case SHOW_HIDER_BUTTON:
        return {
          ...stateParam,
          isShownHiderButton: doSetIsShownHiderButton(false),
        };
      case HIDE_HIDER_BUTTON:
        return {
          ...stateParam,
          isShownHiderButton: doSetIsShownHiderButton(true),
        };
      default:
        return stateParam;
    }
  };

  const initialState = {
    isExpandBanner: doSetIsExpandBanner(isExpandBannerOn),
    toggleExpandBanner: isExpandBannerOn,
    isShownHiderButton: doSetIsShownHiderButton(isShownHiderButtonOn),
    isShownBanner: doSetIsShownBanner(isShownBannerOn),
    isShownBannerMark: isShownBannerOn,
    isExpandBannerMark: isExpandBannerOn,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    isExpandBanner,
    isShownHiderButton,
    isShownBanner,
    isShownBannerMark,
    isExpandBannerMark,
  } = state;

  return (
    <>
      <div
        className={`BannerStickyBox ${isExpandBanner} ${isShownBanner}`}
        style={{ width: widthChange, height }}
        itemRef={`${bannerReference}`}
        isshownbanner={`${isShownBannerMark}`}
        isexpandbanner={`${isExpandBannerMark}`}
        bannerisshownid={`${bannerIsShownId}`}
        bannerexpandedid={`${bannerExpandedId}`}
      >
        <div className={'BannerStickHeaderBoxOuter'} style={{ height }}>
          <div
            className={'BannerStickHeaderBoxText BannerStickHeaderBoxRotate'}
          >
            <div
              id="BannerStickyRetractiveButtonId"
              className={'BannerStickyAreaButtonTextOnTab '}
              onClick={() => dispatch({ type: EXPAND_RETRACTIVE_BANNER })}
            >
              <div className={'BannerStickyTextOnTab'}>{textOnTab}</div>
            </div>
            <div
              id="BannerStickyAreaButtonHideBannerId"
              className={`BannerStickyAreaButtonHideBanner ${isShownHiderButton}`}
              onClick={() => dispatch({ type: HIDE_BANNER })}
            >
              <img
                className={'BannerStickyButtonHideBannerImage'}
                src={closeIconRsr}
                alt="BannerStickyButtonHideBannerImage"
              />
            </div>
          </div>
          <div className={'BannerStickyBoxContent'} style={{ height }}>
            {component}
          </div>
        </div>
      </div>
    </>
  );
};

BannerSticky.propTypes = {
  banner: PropTypes.shape({
    component: PropTypes.any.isRequired,
    textOnTab: PropTypes.string,
    width: PropTypes.string,
    height: PropTypes.string,

    isExpandBannerOn: PropTypes.bool.isRequired,
    isShownHiderButtonOn: PropTypes.bool.isRequired,
    isShownBannerOn: PropTypes.bool.isRequired,

    bannerReference: PropTypes.string.isRequired,

    bannerIsShownId: PropTypes.string,
    bannerExpandedId: PropTypes.string,
  }),
};

export default BannerSticky;
